import {
  AttendanceStubCheckSvg,
  CheckIcon,
  CrossIcon,
  ModalConfirm,
  ModalPanel,
  TableTemplatePng,
} from '@schooly/style';
import { FC, PropsWithChildren } from 'react';
import { TABLE_TEMPLATE_IMG_BY_LOCALE } from './ImportTimetableContent';
import { useIntl } from 'react-intl';
import { Box, Button, Icon, IconButton, Stack, Typography } from '@mui/material';

import type { ValidationErrorReason } from './helpers/validateTimetableFile';

type TryAgainModalProps = {
  onClose: () => void;
  onTryAgain: () => void;
  fileName: string;
  columnNames: string[];
  reason: ValidationErrorReason;
};

export const TryAgainModal: FC<TryAgainModalProps> = ({
  onClose,
  onTryAgain,
  fileName,
  columnNames,
  reason,
}) => {
  const { formatMessage, locale } = useIntl();

  const templateImg = TABLE_TEMPLATE_IMG_BY_LOCALE[locale] ?? TableTemplatePng;
  const missingColumns = reason === 'columns-missing';

  return (
    <ModalWrapper onClose={onClose}>
      <Stack alignItems="center" pt={2.75}>
        <img
          src={templateImg}
          alt={formatMessage({ id: 'school-timetables-Import-TableTemplate' })}
          width={306}
          height={306}
        />
      </Stack>

      <Stack pt={2} maxWidth="500px">
        <Typography variant="h1">
          {formatMessage({ id: 'import-Timetable-CannotImport' }, { fileName })}
        </Typography>

        <Typography variant="h3" pt={1}>
          {formatMessage({
            id: missingColumns
              ? 'import-Timetable-CannotImportColumnsMissing'
              : 'import-Timetable-CannotImportColumnsEmpty',
          })}
        </Typography>

        {missingColumns && (
          <Stack gap={0.25} pl={4}>
            {columnNames.map((columnName) => (
              <Stack key={columnName} direction="row" gap={1} alignItems="center">
                <Box
                  sx={(theme) => ({
                    width: 4,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: theme.palette.primary.main,
                  })}
                />{' '}
                <Typography variant="h3">{columnName}</Typography>
              </Stack>
            ))}
          </Stack>
        )}

        <Typography variant="h3" py={2.5}>
          {formatMessage({
            id: missingColumns
              ? 'import-Timetable-CannotImportColumnsMissingDescription'
              : 'import-Timetable-CannotImportColumnsEmptyDescription',
          })}
        </Typography>

        <Button sx={{ alignSelf: 'flex-start' }} onClick={onTryAgain}>
          {formatMessage({ id: 'import-Timetable-TryAgain' })}
        </Button>
      </Stack>
    </ModalWrapper>
  );
};

type SuccessModalProps = {
  onClose: () => void;
  fileName: string;
  importedRowsCount: number;
  notImportedRowsCount?: number;
  onViewTimetable: () => void;
};

export const SuccessModal: FC<SuccessModalProps> = ({
  onClose,
  fileName,
  importedRowsCount,
  notImportedRowsCount,
  onViewTimetable,
}) => {
  const { formatMessage } = useIntl();
  return (
    <ModalWrapper onClose={onClose}>
      <Stack alignItems="center" pt={2.75}>
        <img
          src={AttendanceStubCheckSvg}
          alt={formatMessage({ id: 'import-Timetable-Success' }, { fileName })}
        />
      </Stack>
      <Stack direction="column" gap={0.5} pt={2.5}>
        <Typography variant="h1">
          {formatMessage({ id: 'import-Timetable-Success' }, { fileName })}
        </Typography>
        <Stack direction="row" gap={1}>
          <Icon sx={{ color: 'success.main' }}>
            <CheckIcon />
          </Icon>

          <Typography variant="h3">
            {formatMessage(
              { id: 'import-Timetable-SuccessRowsImported' },
              { rowsCount: importedRowsCount },
            )}
          </Typography>
        </Stack>
        {!!notImportedRowsCount && (
          <Stack direction="row" gap={1}>
            <Icon sx={{ color: 'error.main' }}>
              <CrossIcon />
            </Icon>

            <Typography variant="h3">
              {formatMessage(
                { id: 'import-Timetable-SuccessRowsNotImported' },
                { rowsCount: notImportedRowsCount },
              )}
            </Typography>
          </Stack>
        )}
        <Button sx={{ alignSelf: 'flex-start', mt: 2 }} onClick={onViewTimetable}>
          {formatMessage({ id: 'import-Timetable-SuccessViewTimetable' })}
        </Button>
      </Stack>
    </ModalWrapper>
  );
};

const ModalWrapper: FC<PropsWithChildren<{ onClose: () => void }>> = ({ children, onClose }) => {
  return (
    <ModalConfirm fullWidth open onClose={onClose}>
      <ModalPanel active>
        <Stack alignItems="center" gap={2.75}>
          <IconButton
            onClick={onClose}
            sx={{
              alignSelf: 'flex-end',
            }}
            inverse
          >
            <CrossIcon />
          </IconButton>
        </Stack>
        {children}
      </ModalPanel>
    </ModalConfirm>
  );
};
